import { useQuery } from "@tanstack/react-query";
import { ListMusic, Music2, Plus } from "lucide-react";
import { useState } from "react";
import { AlbumManager } from "../components/AlbumManager";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CreateAlbumDialog } from "../components/CreateAlbumDialog";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { ProviderTabs } from "../components/ProviderTabs";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { Album } from "../types";

export const Albums = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);

  const { isAuthenticated, login } = useSpotifyAuth();
  const { getAllAlbums, deleteAlbum, addAlbum, isContentLiked, toggleLike } = useLocalLibraryStore();
  const localAlbums = useLocalLibraryStore((state) => state.albums); // Get directly from state instead of using getAllAlbums

  const { data: spotifyAlbums, isLoading } = useQuery({
    queryKey: ["saved-albums"],
    queryFn: () => spotifyApi.currentUser.albums.savedAlbums(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const handleDelete = () => {
    if (selectedAlbum) {
      deleteAlbum(selectedAlbum);
      setSelectedAlbum(null);
    }
  };

  const handleCreateAlbum = async (data: { name: string; artist: string; description?: string; coverUrl?: string }) => {
    try {
      const id = await addAlbum(data.name, data.artist, data.description, data.coverUrl);
      setIsCreateDialogOpen(false);
      // Optionally, you could navigate to the new album
      // navigate(`/albums/${id}`);
    } catch (error) {
      console.error("Error creating album:", error);
    }
  };

  const renderContent = () => {
    if (activeProvider === "spotify") {
      if (!isAuthenticated) {
        return (
          <EmptyState
            icon={ListMusic}
            title="Connect to Spotify"
            description="Connect your Spotify account to access your albums"
            action={{
              label: "Connect Spotify",
              onClick: login,
            }}
          />
        );
      }

      if (isLoading) {
        return (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        );
      }

      if (!spotifyAlbums?.items?.length) {
        return (
          <EmptyState
            icon={Music2}
            title="No albums found"
            description="Save some albums on Spotify to see them here"
            action={{
              label: "Browse Music",
              href: "/search",
            }}
          />
        );
      }

      return (
        <MediaGrid
          items={spotifyAlbums.items.map((item) => ({
            id: item.album.id,
            name: item.album.name,
            artist: item.album.artists[0].name,
            coverUrl: item.album.images[0]?.url,
            trackCount: item.album.total_tracks,
            source: "spotify" as const,
            type: "album" as const,
            isLiked: isContentLiked(item.album.id, "album"),
            onLike: () => toggleLike(item.album.id, "album", "spotify"),
          }))}
        />
      );
    }

    if (!localAlbums?.length) {
      return (
        <EmptyState
          icon={Music2}
          title="Create your first album"
          description="Start organizing your music into albums"
          action={{
            label: "Create Album",
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      );
    }

    return (
      <MediaGrid
        items={localAlbums.map((album: Album) => ({
          id: album.id,
          name: album.name,
          artist: album.tracks[0]?.artist || "Various Artists",
          coverUrl: album.coverUrl || album.tracks[0]?.albumArt,
          trackCount: album.tracks.length,
          source: "local" as const,
          type: "album" as const,
          isLiked: isContentLiked(album.id, "album"),
          onLike: () => toggleLike(album.id, "album", "local"),
          onEdit: () => {
            setSelectedAlbum(album.id);
            setShowEditDialog(true);
          },
          onDelete: () => {
            setSelectedAlbum(album.id);
            setShowDeleteConfirm(true);
          },
        }))}
      />
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Your Albums</h1>
          <p className="text-zinc-400">
            {activeProvider === "spotify" ? spotifyAlbums?.items?.length || 0 : localAlbums.length} album
            {(activeProvider === "spotify" ? spotifyAlbums?.items?.length : localAlbums.length) !== 1 ? "s" : ""}
          </p>
        </div>

        {activeProvider === "local" && (
          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition"
          >
            <Plus className="w-5 h-5" />
            Create Album
          </button>
        )}
      </div>

      <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

      {renderContent()}

      <CreateAlbumDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateAlbum}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedAlbum(null);
        }}
        onConfirm={handleDelete}
        title="Delete Album"
        message="Are you sure you want to delete this album? This action cannot be undone."
        confirmLabel="Delete"
        type="danger"
      />

      {selectedAlbum && (
        <AlbumManager
          albumName={selectedAlbum}
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedAlbum(null);
          }}
        />
      )}
    </div>
  );
};
